import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { LoadingWrapper } from './ui/withLoading';
import { SkeletonSessionCard, SkeletonInsightCard } from './ui/skeletons';
import { FadeInUp, AnimatedContainer, AnimatedItem } from './ui/animated';
import { checkSubscriptionSilently } from '../lib/utils';
import { debugLog, debugWarn, errorLog } from '../utils/debug';
import { motion } from 'framer-motion';
import './SessionSummary.css';

export default function SessionSummary({ chatId: propChatId }) {
  const { chatId: urlChatId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Use prop chatId if provided, otherwise use URL param
  const chatId = propChatId || urlChatId;

  useEffect(() => {
    if (currentUser && chatId) {
      // Silently check subscription status
      checkSubscriptionSilently(currentUser, navigate).then(isValid => {
        if (isValid) {
          loadSummary();
        }
      });
    }
  }, [currentUser, chatId]);

  useEffect(() => {
    // Add entrance animations
    const cards = document.querySelectorAll('.session-insight-card');
    cards.forEach((card, i) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(18px)';
      card.style.transition = 'opacity .4s ease, transform .4s ease';
      setTimeout(() => {
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, 50 + (i * 45));
    });
  }, [summary]);

  const loadSummary = async (retryCount = 0) => {
    try {
      debugLog(`🔍 Loading summary for chat: ${chatId} (attempt ${retryCount + 1})`);
      
      // Load directly from Firestore subcollection (sole source of truth)
      const db = getFirestore();
      const chatDocRef = doc(db, 'users', currentUser.uid, 'chats', chatId);
      const chatDoc = await getDoc(chatDocRef);
      
      if (chatDoc.exists()) {
        const chatData = chatDoc.data();
        debugLog('📄 Chat document data:', chatData);
        
        if (chatData.status === 'ready' && chatData.summary) {
          // Chat is ready with summary
          setSummary({
            id: chatDoc.id,
            status: chatData.status,
            startedAt: chatData.started_at || chatData.startedAt,
            endedAt: chatData.ended_at || chatData.endedAt,
            summary: chatData.summary,
            timestamp: chatData.ended_at || chatData.endedAt || chatData.timestamp,
            updatedAt: chatData.updatedAt
          });
          
          debugLog(`✅ Loaded ready summary from Firestore: ${chatDoc.id}`);
          debugLog('📅 Date fields available:', {
            started_at: chatData.started_at,
            startedAt: chatData.startedAt,
            ended_at: chatData.ended_at,
            endedAt: chatData.endedAt,
            timestamp: chatData.timestamp
          });
          setLoading(false);
          return;
          
        } else if (chatData.status === 'pending' || chatData.status === 'processing') {
          // Chat is still being processed, retry after delay
          debugLog(`⏳ Chat still processing (${chatData.status}), retrying...`);
          
          if (retryCount < 10) { // Max 10 retries (20 seconds)
            setTimeout(() => {
              loadSummary(retryCount + 1);
            }, 2000);
            return;
          } else {
            debugWarn('⚠️ Max retries reached, showing pending status');
            setSummary({
              id: chatDoc.id,
              status: chatData.status,
              summary: null,
              loading: true
            });
            setLoading(false);
            return;
          }
        } else {
          debugLog('⚠️ Chat has unexpected status:', chatData.status);
        }
      } else {
        debugLog('❌ Chat document not found in Firestore subcollection');
      }
      
      // If we get here, try fallback to legacy data
      debugLog('🔄 Trying fallback to legacy arrays...');
      await loadFromLegacyArrays();
      
    } catch (error) {
      errorLog('❌ Error loading summary:', error);
      setError('Failed to load chat summary');
      setLoading(false);
    }
  };

  const loadFromLegacyArrays = async () => {
    try {
      const db = getFirestore();
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        let foundSummary = null;
        
        // Search in chatSummaries array (legacy)
        if (userData.chatSummaries && Array.isArray(userData.chatSummaries)) {
          foundSummary = userData.chatSummaries.find(summary => 
            summary.id === chatId && summary.status === 'ready'
          );
        }
        
        // Search in tempChats array (fallback storage)
        if (!foundSummary && userData.tempChats && Array.isArray(userData.tempChats)) {
          foundSummary = userData.tempChats.find(chat => 
            chat.id === chatId && (chat.status === 'ready' || chat.status === 'pending')
          );
        }
        
        // Fallback to pastConversations (legacy)
        if (!foundSummary && userData.pastConversations && Array.isArray(userData.pastConversations)) {
          const summaryConversation = userData.pastConversations.find(conv => 
            conv.chatId === chatId && conv.type === 'summary' && conv.status === 'ready'
          );
          if (summaryConversation) {
            foundSummary = {
              id: summaryConversation.chatId,
              status: summaryConversation.status,
              ended_at: summaryConversation.timestamp,
              summary: summaryConversation.summary,
              timestamp: summaryConversation.timestamp,
              started_at: summaryConversation.started_at || null
            };
          }
        }
        
        if (foundSummary) {
          setSummary(foundSummary);
          debugLog(`📄 Loaded summary from legacy arrays: ${chatId}`);
        } else {
          setError('Summary not found');
        }
      } else {
        setError('User data not found');
      }
    } catch (err) {
      errorLog('Error loading summary:', err);
      setError('Error loading summary');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    debugLog('📅 formatDate input:', dateString, 'Type:', typeof dateString);
    
    let date;
    try {
      // Handle Firestore Timestamp objects
      if (dateString && typeof dateString === 'object' && dateString.toDate) {
        date = dateString.toDate();
      } else if (dateString && typeof dateString === 'object' && dateString.seconds) {
        // Handle Firestore Timestamp with seconds
        date = new Date(dateString.seconds * 1000);
      } else {
        // Handle string dates
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        debugWarn('⚠️ Invalid date:', dateString);
        return 'Invalid date';
      }
      
      debugLog('📅 Parsed date:', date);
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      errorLog('❌ Error formatting date:', error, 'Input:', dateString);
      return 'Date unavailable';
    }
  };

  const calculateDaysSince = (endDate) => {
    if (!endDate) return 'Unknown';
    
    let date;
    try {
      // Handle Firestore Timestamp objects
      if (endDate && typeof endDate === 'object' && endDate.toDate) {
        date = endDate.toDate();
      } else if (endDate && typeof endDate === 'object' && endDate.seconds) {
        // Handle Firestore Timestamp with seconds
        date = new Date(endDate.seconds * 1000);
      } else {
        // Handle string dates
        date = new Date(endDate);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        debugWarn('⚠️ Invalid date for days calculation:', endDate);
        return 'Unknown';
      }
      
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
      
    } catch (error) {
      errorLog('❌ Error calculating days since:', error, 'Input:', endDate);
      return 'Unknown';
    }
  };

  const handleClose = () => {
    navigate('/chats');
  };

  const handleContinueChat = () => {
    navigate('/chat');
  };

  const handleViewClick = (e) => {
    e.target.style.opacity = '0.5';
    setTimeout(() => {
      e.target.style.opacity = '';
    }, 100);
  };

  const renderTakeaways = (takeaways) => {
    if (!takeaways) {
      debugLog('📝 No takeaways data found');
      return (
        <div className="takeaway-item">
          <div className="takeaway-bullet">•</div>
          <div className="takeaway-text">No takeaways available for this session.</div>
        </div>
      );
    }
    
    debugLog('📝 Takeaways data:', takeaways, 'Type:', typeof takeaways);
    
    // Handle different formats
    let takeawaysArray;
    
    if (Array.isArray(takeaways)) {
      takeawaysArray = takeaways;
    } else if (typeof takeaways === 'string') {
      // If it's a string, split by newlines or bullet points
      takeawaysArray = takeaways.split(/\n|•/).filter(item => item.trim()).map(item => item.trim());
    } else if (typeof takeaways === 'object') {
      // If it's an object, try to get values
      takeawaysArray = Object.values(takeaways).filter(item => typeof item === 'string');
    } else {
      takeawaysArray = [takeaways.toString()];
    }
    
    debugLog('📝 Processed takeaways array:', takeawaysArray);
    
    if (!takeawaysArray.length) {
      return (
        <div className="takeaway-item">
          <div className="takeaway-bullet">•</div>
          <div className="takeaway-text">No takeaways available for this session.</div>
        </div>
      );
    }
    
    return takeawaysArray.map((takeaway, index) => (
      <div key={index} className="takeaway-item">
        <div className="takeaway-bullet">•</div>
        <div className="takeaway-text">{takeaway}</div>
      </div>
    ));
  };

  const renderInsights = (insights) => {
    if (!insights || !Array.isArray(insights)) return null;
    
    return insights.map((insight, index) => (
      <div key={index}>
        <div className="session-insight-card">
          <div className="session-quote-icon">"</div>
          <div className="session-quote-content">
            <div className="session-quote-text">
              {insight.user_words || insight.quote || 'Key insight'}
            </div>
          </div>
        </div>
        <div className="session-insight-text">
          {insight.elaboration || insight.explanation || 'Detailed explanation of this insight.'}
        </div>
      </div>
    ));
  };

  if (loading) {
    return (
      <LoadingWrapper 
        loading={loading} 
        skeleton={
          <div className="session-container">
            <div className="phone-container">
              <div className="content">
                <div className="session-header">
                  <div className="session-nav">
                    <div className="h-8 rounded w-8" style={{ backgroundColor: 'rgba(18, 51, 47, 0.1)' }}></div>
                    <div className="h-6 rounded w-20" style={{ backgroundColor: 'rgba(18, 51, 47, 0.1)' }}></div>
                  </div>
                  <div className="session-main-title">
                    <div className="h-7 rounded w-3/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.15)' }}></div>
                  </div>
                  <div className="session-date">
                    <div className="h-4 rounded w-1/3" style={{ backgroundColor: 'rgba(142, 142, 147, 0.2)' }}></div>
                  </div>
                  <div className="h-12 rounded w-full" style={{ backgroundColor: 'rgba(18, 51, 47, 0.1)' }}></div>
                </div>
                <div className="session-content">
                  <div className="session-section">
                    <div className="h-6 rounded w-1/4 mb-4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.15)' }}></div>
                    <div className="rounded-2xl p-4 mb-6" style={{ backgroundColor: 'rgba(230, 244, 234, 0.6)', border: '1px solid rgba(18, 51, 47, 0.1)' }}>
                      <div className="space-y-3">
                        <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }}></div>
                        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="session-section">
                    <div className="h-6 rounded w-1/3 mb-4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.15)' }}></div>
                    <div className="space-y-3">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="h-3 rounded w-3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }}></div>
                          <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }}></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="session-section">
                    <div className="h-6 rounded w-1/4 mb-4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.15)' }}></div>
                    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                      {Array.from({ length: 2 }).map((_, index) => (
                        <div key={index} className="rounded-2xl p-4" style={{ backgroundColor: 'rgba(230, 244, 234, 0.6)', border: '1px solid rgba(18, 51, 47, 0.1)' }}>
                          <div className="h-6 rounded w-6 mb-3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }}></div>
                          <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }}></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <div className="session-container">
          <div className="phone-container">
            <div className="content">
              <div className="loading-state">
                <div className="loading-icon">📄</div>
                <div className="loading-text">Loading session summary...</div>
              </div>
            </div>
          </div>
        </div>
      </LoadingWrapper>
    );
  }

  if (summary && (summary.status === 'pending' || summary.status === 'processing')) {
    return (
      <div className="session-container">
        <div className="phone-container">
          <div className="content">
            <div className="processing-state">
              <div className="processing-icon">⏳</div>
              <div className="processing-title">Summary Being Generated</div>
              <div className="processing-text">
                Your chat is being analyzed and summarized. This usually takes 10-15 seconds.
              </div>
              <div className="processing-spinner">
                <div className="spinner-ring"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="session-container">
        <div className="phone-container">
          <div className="content">
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <div className="error-title">Error Loading Summary</div>
              <div className="error-subtitle">{error}</div>
              <button className="back-btn" onClick={handleClose}>
                Back to History
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="session-container">
        <div className="phone-container">
          <div className="content">
            <div className="error-state">
              <div className="error-icon">📄</div>
              <div className="error-title">Summary Not Found</div>
              <div className="error-subtitle">The requested chat summary could not be found.</div>
              <button className="back-btn" onClick={handleClose}>
                Back to History
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const summaryData = summary.summary || {};
  const formattedDate = formatDate(summary.endedAt || summary.ended_at || summary.timestamp);
  const daysSince = calculateDaysSince(summary.endedAt || summary.ended_at || summary.timestamp);
  
  // Debug logging for date values
  debugLog('📅 Date debugging:', {
    endedAt: summary.endedAt,
    ended_at: summary.ended_at,
    timestamp: summary.timestamp,
    finalDate: summary.endedAt || summary.ended_at || summary.timestamp,
    formattedDate: formattedDate,
    daysSince: daysSince
  });
  
  return (
    <div className="session-container">
      <div className="phone-container">
        <div className="content">
          <motion.div 
            className="session-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
          >
            <div className="session-nav">
              <div className="session-main-title">
                {summaryData.title || 'Chat Session Summary'}
              </div>
              <button className="close-btn" onClick={handleClose}>✕</button>
            </div>
            <div className="session-date">{formattedDate}</div>
            <button className="session-primary-btn" onClick={handleContinueChat}>
              Let's talk about it
            </button>
          </motion.div>

          <motion.div 
            className="session-content"
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <motion.div 
              className="session-section"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
            >
              <div className="session-section-title">Summary</div>
              <div className="session-summary-card">
                <div className="session-summary-text">
                  {summaryData.public_summary || 'No summary available.'}
                </div>
              </div>
            </motion.div>

            {/* Key Takeaways */}
            <motion.div 
              className="session-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <div className="session-section-title">Key Takeaways</div>
              <div className="takeaways-grid">
                {summaryData.takeaways?.map((takeaway, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.25 + (index * 0.05) }}
                  >
                    <div className="takeaway-card">
                      <div className="takeaway-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.5 3 8 9l4 13 4-13-2.5-6"/>
                          <path d="M17 3a2 2 0 0 1 1.6.8l3 4a2 2 0 0 1 .013 2.382l-7.99 10.986a2 2 0 0 1-3.247 0l-7.99-10.986A2 2 0 0 1 2.4 7.8l2.998-3.997A2 2 0 0 1 7 3z"/>
                          <path d="M2 9h20"/>
                        </svg>
                      </div>
                      <div className="takeaway-text">{takeaway}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Insights */}
            {summaryData.key_insights && summaryData.key_insights.length > 0 && (
              <motion.div 
                className="session-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <div className="session-section-title">Insights</div>
                <div className="insights-grid">
                  {summaryData.key_insights.map((insight, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.35 + (index * 0.05) }}
                    >
                      <div className="insight-card">
                        <div className="insight-icon">💡</div>
                        <div className="insight-text">
                          <div className="insight-quote">"{insight.user_words || 'Key insight'}"</div>
                          <div className="insight-elaboration">{insight.elaboration || 'Detailed explanation of this insight.'}</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Action Items */}
            {summaryData.action_steps && summaryData.action_steps.length > 0 && (
              <motion.div 
                className="session-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
              >
                <div className="session-section-title">Your action steps</div>
                <div className="actions-grid">
                  {summaryData.action_steps.map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.45 + (index * 0.05) }}
                    >
                      <div className="action-card">
                        <div className="action-icon">✓</div>
                        <div className="action-text">{item}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Mood Tracking */}
            {summaryData.mood && (
              <motion.div 
                className="session-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
              >
                <div className="session-section-title">Mood Check-in</div>
                <div className="mood-display">
                  <div className="mood-emoji">{summaryData.mood.emoji}</div>
                  <div className="mood-text">{summaryData.mood.description}</div>
                  <div className="mood-score">Score: {summaryData.mood.score}/10</div>
                </div>
              </motion.div>
            )}
          </motion.div>

          <motion.div 
            className="session-bottom-btn"
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <button className="session-continue-btn" onClick={handleContinueChat}>
              Let's talk about it
            </button>
          </motion.div>
        </div>

        <div className="safe-area"></div>
      </div>
    </div>
  );
}